import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import { supabase } from "@/lib/supabase";
import {
  useSEOMeta,
  generateBreadcrumbSchema,
  generateWebPageSchema,
  generateFAQPageSchema,
} from "@/components/SEO";

const otherAreas = [
  { title: "Health", href: "/our-work/health", icon: "ri-heart-pulse-line", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { title: "Education", href: "/our-work/education", icon: "ri-book-open-line", color: "bg-amber-50 text-amber-700 border-amber-200" },
  { title: "Economic Security", href: "/our-work/economic-security", icon: "ri-seedling-line", color: "bg-teal-50 text-teal-700 border-teal-200" },
];

export default function ProtectionPage() {
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useSEOMeta({
    title: "Protection - BHODEV Cameroon | GBV Prevention, Human Rights & Child Protection",
    description:
      "BHODEV ensures displaced people and communities in Cameroon are protected from violence and rights violations. GBV prevention, child protection, psycho-social support, legal aid, and human rights advocacy across North West and South West regions.",
    keywords:
      "BHODEV protection, GBV prevention Cameroon, gender based violence Cameroon, child protection Cameroon, human rights advocacy Cameroon, psycho social support, legal aid Cameroon, displaced people protection, SDG 16 Cameroon",
    canonicalUrl: "https://bhodevcameroon.org/our-work/protection",
    ogImage:
      "https://readdy.ai/api/search-image?query=community%20protection%20workers%20supporting%20displaced%20families%20in%20Cameroon%20safe%20space%20for%20women%20and%20children%20humanitarian%20aid%20warm%20compassionate%20documentary%20photography%20wide%20landscape&width=1200&height=630&seq=protection-og&orientation=landscape",
    schemaData: {
      ...generateBreadcrumbSchema([
        { name: "Home", url: "https://bhodevcameroon.org/" },
        { name: "Our Work", url: "https://bhodevcameroon.org/our-work" },
        { name: "Protection", url: "https://bhodevcameroon.org/our-work/protection" },
      ]),
      ...generateWebPageSchema(
        "Protection - BHODEV Cameroon",
        "BHODEV ensures displaced people and communities in Cameroon are protected from violence and rights violations through GBV prevention, child protection, and human rights advocacy.",
        "https://bhodevcameroon.org/our-work/protection"
      ),
      ...generateFAQPageSchema([
        {
          question: "What protection services does BHODEV provide?",
          answer:
            "BHODEV provides human rights advocacy, gender-based violence prevention and response, child protection, psycho-social support, and legal aid services to displaced and vulnerable communities in Cameroon's North West and South West regions.",
        },
        {
          question: "How does BHODEV prevent gender-based violence?",
          answer:
            "BHODEV prevents and responds to gender-based violence through safe spaces, legal assistance, and psycho-social support for survivors in crisis-affected communities across Cameroon.",
        },
      ]),
    },
  });

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("projects")
      .select("*")
      .eq("sector", "Protection")
      .order("created_at", { ascending: false });
    if (error) {
      console.error("Error fetching protection projects:", error);
    } else {
      setProjects(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const fallbackProjects = [
    {
      title: "Human Rights Advocacy Campaign",
      category: "Human Rights",
      description: "Raising awareness of humanitarian crises and the needs of affected people, advocating for protection of civilians and ensuring all activities meet rigorous standards.",
      year: "2023",
      image: "https://readdy.ai/api/search-image?query=human%20rights%20advocacy%20campaign%20in%20Cameroon%20community%20members%20and%20NGO%20workers%20raising%20awareness%20peaceful%20gathering%20documentary%20photography%20warm%20light&width=400&height=260&seq=pp1&orientation=landscape",
    },
    {
      title: "GBV Prevention & Response",
      category: "Gender-Based Violence",
      description: "Preventing and responding to gender-based violence through safe spaces, legal assistance, and psycho-social support for survivors in crisis-affected communities.",
      year: "2023",
      image: "https://readdy.ai/api/search-image?query=gender%20based%20violence%20prevention%20programme%20in%20Cameroon%20women%20support%20group%20safe%20space%20counselling%20session%20compassionate%20humanitarian%20setting&width=400&height=260&seq=pp2&orientation=landscape",
    },
    {
      title: "Child Protection Programme",
      category: "Child Protection",
      description: "Protecting children from violence, exploitation and abuse by creating safe spaces for boys and girls and providing psycho-social support.",
      year: "2022",
      image: "https://readdy.ai/api/search-image?query=child%20protection%20programme%20in%20Cameroon%20children%20in%20safe%20play%20space%20supervised%20by%20protection%20workers%20joyful%20atmosphere%20warm%20natural%20light&width=400&height=260&seq=pp3&orientation=landscape",
    },
    {
      title: "Legal Aid & Access to Justice",
      category: "Legal Aid",
      description: "Helping people access their rights through legal assistance work, ensuring conflict and displacement affected people enjoy their human rights.",
      year: "2022",
      image: "https://readdy.ai/api/search-image?query=legal%20aid%20programme%20in%20Cameroon%20community%20members%20receiving%20legal%20assistance%20from%20NGO%20workers%20professional%20consultation%20setting%20warm%20light&width=400&height=260&seq=pp4&orientation=landscape",
    },
  ];

  const displayProjects = projects.length > 0 ? projects : fallbackProjects;

  return (
    <div className="min-h-screen bg-white font-inter">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[460px] md:h-[520px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://firebasestorage.googleapis.com/v0/b/bhodevcameroon.appspot.com/o/project-assets%2Fsensitization-on-breast-cancer-in-buea-municiaplity%2FIMG-20231023-WA0014.jpg?alt=media&token=f4beab55-1d74-445d-a6c6-bd39ce421520"
            alt="BHODEV Protection Work in Cameroon - GBV Prevention and Human Rights"
            title="BHODEV Protection Work in Cameroon - GBV Prevention and Human Rights"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/65"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto w-full">
          <nav className="flex items-center justify-center gap-2 text-xs text-white/60 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white transition-colors duration-200">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/our-work" className="hover:text-white transition-colors duration-200">Our Work</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white/90">Protection</span>
          </nav>
          <span className="inline-block bg-rose-500/20 border border-rose-400/40 text-rose-300 text-xs font-semibold px-4 py-1.5 rounded-full mb-5 tracking-wider uppercase">Core Sector</span>
          <h1 className="text-4xl md:text-5xl font-bold mb-5 leading-tight">Protection</h1>
          <p className="text-lg md:text-xl text-white/85 max-w-2xl mx-auto leading-relaxed">
            The need to protect people has become a part and parcel of life in a crisis environment as people are forced to flee their homes.
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-14 px-4 md:px-8 lg:px-12 bg-rose-50">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-10 items-start">
            <div className="w-full lg:w-3/5">
              <span className="text-xs font-semibold uppercase tracking-widest text-rose-700 mb-3 block">Core Sectors — Protection</span>
              <h2 className="text-2xl md:text-3xl font-bold text-stone-800 mb-5">Our Approach to Protection</h2>
              <p className="text-stone-700 text-sm md:text-base leading-relaxed mb-4">
                The need to protect people has become a part and parcel of life in a crisis environment as people are forced to flee their homes. They often experience ongoing threats to their safety. <strong>BHODEV stands to ensure that displaced people and communities are protected</strong>, by preventing and responding to violence, coercion, and actions taken by others to deny them of their rights.
              </p>
            </div>
            <div className="w-full lg:w-2/5 grid grid-cols-2 gap-3">
              {[
                { icon: "ri-shield-user-line", label: "Protection Components", value: "6" },
                { icon: "ri-women-line", label: "Women Supported", value: "850+" },
                { icon: "ri-hand-heart-line", label: "PSS Sessions", value: "100+" },
                { icon: "ri-global-line", label: "SDG Aligned", value: "SDG 16" },
              ].map((stat) => (
                <div key={stat.label} className="bg-white rounded-xl p-4 border border-rose-100 text-center">
                  <div className="w-8 h-8 flex items-center justify-center text-rose-600 mx-auto mb-2">
                    <i className={`${stat.icon} text-xl`}></i>
                  </div>
                  <p className="text-lg font-bold text-stone-800">{stat.value}</p>
                  <p className="text-xs text-stone-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-14 px-4 md:px-8 lg:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
            <div>
              <h3 className="text-lg font-bold text-stone-800 mb-4 uppercase tracking-wide">Our Expertise in Protection from Violence</h3>
              <p className="text-stone-600 text-sm leading-relaxed mb-4">
                Protection in its widest context encompasses all activities aimed at ensuring full respect for the rights of the individual in accordance with the letter and the spirit of the relevant bodies of law. That is ensuring the safety, dignity and rights of people in the society from the violence or abuse during an armed conflict or natural disaster. BHODEV work to reduce the risks faced by displaced people (boys, girls, refugees, returnees, IDPs and Host communities) while strengthening their capacity for self-protection through the creation of safe space for girls, boys, women and host communities.
              </p>
              <p className="text-stone-600 text-sm leading-relaxed mb-4">
                Protection in its widest context has always been part of our core activities as people who are forced to flee their homes to escape conflict or persecution must often leave everything behind to make the dangerous journey to safety. Whether we are helping people access their rights through our legal assistance work, protecting children by providing safe spaces to learn and play, or providing families with a safe place to live, <strong>protection is always at the heart of what we do.</strong>
              </p>
              <p className="text-stone-600 text-sm leading-relaxed">
                Through our private and public advocacy, we raise awareness of humanitarian crises and the needs of affected people. We advocate for protection of civilians and we ensure that all our activities, our programmes as well as our policy and advocacy work, highly contribute to protection outcomes and meet rigorous standards to ensure that the people we serve are safe, participatory, accountable and dignified for the communities.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-bold text-stone-800 mb-4 uppercase tracking-wide">BHODEV's Protection Response</h3>
              <p className="text-stone-600 text-sm leading-relaxed mb-4">
                Our main priority is to focus our work on prevention and response to violence more sharply to ensure that conflict and displacement affected people enjoy their human rights and have no displacement-related protection needs.
              </p>
              <p className="text-stone-600 text-sm leading-relaxed mb-5">
                BHODEV's responses in the Protection Sector consist of the following components:
              </p>
              <ul className="flex flex-col gap-2.5 mb-6">
                {[
                  "Advocating for Human Right (HR)",
                  "Promoting the Rights of Women and Girls (RWG)",
                  "Prevention of and Response against Gender-Based-Violence (GBV)",
                  "Promote Child Protection (CP)",
                  "Psycho-Social Support (PSS)",
                  "Legal Aids",
                ].map((item, i) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-stone-600">
                    <span className="w-5 h-5 flex items-center justify-center bg-rose-100 text-rose-700 rounded-full text-xs font-bold shrink-0 mt-0.5">{i + 1}</span>
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-stone-600 text-sm leading-relaxed">
                We work to mobilise and influence the humanitarian community to invest in specialised protection services and consciously contribute to better protection outcomes. We engage in protection monitoring and take leadership roles in protection coordination systems and policy mechanisms with the <strong>ultimate goal of saving lives and reduce the impact of conflicts and natural disasters on the victims.</strong>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Projects */}
      <section className="py-14 px-4 md:px-8 lg:px-12 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-stone-800 mb-2">Organization Projects under Protection</h2>
          <p className="text-stone-500 text-sm mb-10">Programmes and initiatives driving protection outcomes in Cameroon</p>

          {loading && (
            <div className="flex items-center justify-center py-16">
              <i className="ri-loader-4-line animate-spin text-rose-600 text-2xl"></i>
            </div>
          )}

          {!loading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {displayProjects.map((project) => (
                <Link
                  key={project.id || project.title}
                  to={project.id ? `/projects/${project.id}` : "#"}
                  className="bg-white rounded-2xl overflow-hidden border border-stone-100 hover:border-rose-200 transition-all duration-300 group cursor-pointer block"
                >
                  <div className="w-full h-[160px]">
                    <img
                      src={project.image_url || project.image}
                      alt={project.title}
                      title={project.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <span className="inline-block bg-rose-100 text-rose-700 text-xs font-semibold px-2.5 py-1 rounded-full mb-3">
                      {project.status || project.category}
                    </span>
                    <h3 className="text-sm font-bold text-stone-800 mb-2 leading-snug">{project.title}</h3>
                    <p className="text-xs text-stone-500 leading-relaxed line-clamp-3">
                      {project.description || project.excerpt || ""}
                    </p>
                    <p className="text-xs text-stone-400 mt-3 font-medium">{project.start_date || project.year}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Other Core Areas */}
      <section className="py-12 px-4 md:px-8 lg:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-lg font-bold text-stone-800 mb-6">Explore Other Core Areas</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {otherAreas.map((area) => (
              <Link
                key={area.title}
                to={area.href}
                className={`flex items-center gap-3 px-5 py-4 rounded-xl border ${area.color} hover:scale-105 transition-all duration-200 cursor-pointer`}
              >
                <div className="w-8 h-8 flex items-center justify-center shrink-0">
                  <i className={`${area.icon} text-xl`}></i>
                </div>
                <span className="font-semibold text-sm">{area.title}</span>
                <i className="ri-arrow-right-line ml-auto text-sm"></i>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}